import { Download } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';

interface DownloadTemplateMenuProps {
  /** Static asset path under `public/`, e.g. `/templates/tasks-import-template.csv`. */
  csvHref: string;
  /** Static asset path under `public/`, e.g. `/templates/tasks-import-template.xlsx`. */
  xlsxHref: string;
  csvLabel: string;
  xlsxLabel: string;
  /** Trigger's visible label (labeled variant) or aria-label/tooltip text (icon variant). */
  triggerLabel: string;
  /** Prefix for `data-testid`s, e.g. `tasks-toolbar` → `tasks-toolbar-download-template-csv`. */
  testIdPrefix: string;
  /**
   * `icon` (default): icon-only trigger with a Tooltip, for toolbars with icon buttons.
   * `labeled`: icon + visible text, for toolbars whose sibling actions show a label.
   */
  triggerVariant?: 'icon' | 'labeled';
}

/**
 * A "download template" dropdown for a bulk-import action, offering one
 * format per menu item. Sits next to the Import trigger so the templates are
 * discoverable without opening the import dialog first. Templates are static
 * files served from `public/` — `download` on the anchor makes the browser
 * save the file instead of navigating to it.
 */
export function DownloadTemplateMenu({
  csvHref,
  xlsxHref,
  csvLabel,
  xlsxLabel,
  triggerLabel,
  testIdPrefix,
  triggerVariant = 'icon',
}: DownloadTemplateMenuProps) {
  return (
    <DropdownMenu>
      {triggerVariant === 'labeled' ? (
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            data-testid={`${testIdPrefix}-download-template-trigger`}
          >
            <Download className="size-4" />
            {triggerLabel}
          </Button>
        </DropdownMenuTrigger>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={triggerLabel}
                data-testid={`${testIdPrefix}-download-template-trigger`}
              >
                <Download className="size-4" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>{triggerLabel}</TooltipContent>
        </Tooltip>
      )}

      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild data-testid={`${testIdPrefix}-download-template-csv`}>
          <a href={csvHref} download>
            {csvLabel}
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild data-testid={`${testIdPrefix}-download-template-xlsx`}>
          <a href={xlsxHref} download>
            {xlsxLabel}
          </a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
